import { DragDropModule } from '@angular/cdk/drag-drop';
import { CdkScrollableModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';

import { LexiconModule } from '@galaxy/lexicon';
import { TranslateModule } from '@ngx-translate/core';

import { CoreModule } from '@galaxy/core';

import { GalaxyButtonGroupModule } from '@vendasta/galaxy/button-group';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { GalaxyResizeModule } from '@vendasta/galaxy/resize';
import { GalaxySnackbarModule } from '@vendasta/galaxy/snackbar-service';
import { GalaxyUploaderModule } from '@vendasta/galaxy/uploader';

import { ButtonBlockComponent } from './blocks/button-block/button-block.component';
import { ContactCardComponent } from './blocks/contact-card/contact-card.component';
import { DividerBlockComponent } from './blocks/divider-block/divider-block.component';
import { HeadingBlockComponent } from './blocks/heading-block/heading-block.component';
import { HtmlBlockComponent } from './blocks/html-block/html-block.component';
import { ImageAndTextBlockComponent } from './blocks/image-and-text-block/image-and-text-block.component';
import { ImageBlockComponent } from './blocks/image-block/image-block.component';
import { ReviewRequestLinksBlockComponent } from './blocks/review-request-links-block/review-request-links-block.component';
import { RichTextBlockComponent } from './blocks/rich-text-block/rich-text-block.component';
import { SimpleTableBlockComponent } from './blocks/simple-table-block/simple-table-block.component';
import { SpacerBlockComponent } from './blocks/spacer-block/spacer-block.component';

import {
  EmailAdvancedSettingsComponent,
  EmailSetup,
} from './components/email-advanced-settings/email-advanced-settings.component';
import { EmailBlockPaddingComponent } from './components/email-block-padding/email-block-padding.component';
import { EmailBlocksListComponent } from './components/email-blocks-list/email-blocks-list.component';
import { RichTextEditorComponent } from './components/rich-text-editor/rich-text-editor.component';
import { UnsavedChangesDialogComponent } from './components/unsaved-changes-dialog/unsaved-changes-dialog.component';

import { HoldShiftToChangeNumberFieldByTenDirective } from './directives/hold-shift-to-change-number-field-by-ten.directive';

import { EmailEditorPreviewModule } from '../../email-editor-preview';
import { EmailUII18nModule } from '../../src/lib/i18n/email-ui-i18n.module';
import { DynamicComponentSelectorModule } from './components/dynamic-component-selector';
import { EmailBuilderComponent } from './email-builder.component';
import { EmailContentData } from './types/contentblocks';

import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSelectModule } from '@angular/material/select';
import { ImageGenerationDialogComponent } from '@galaxy/content-generation';
import { ContentGenerationService } from '@vendasta/email-builder';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { GalaxyButtonLoadingIndicatorModule } from '@vendasta/galaxy/button-loading-indicator';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { ContainsValidDynamicContentDirective } from '@vendasta/shared';
import { NpsBlockComponent } from './blocks/nps-block/nps-block.component';
import { EMAIL_BUILDER_ENABLED_RECIPES } from './components/email-blocks-list/email-blocks-list.component';
import { SuggestContentComponent } from './components/suggest-content-dialog/suggest-content-dialog.component';
import {
  AppListingServiceInterface,
  EMAIL_BUILDER_APP_LISTING_SERVICE_TOKEN,
} from './services/app-listing-service.interface';
import { FeatureFlagEnabledService } from './services/feature-flag.service';
import { HasUnsavedChangesGuard } from './services/has-unsaved-changes.guard';
import { EMAILBUILDER_UPLOADER_SERVICE_TOKEN, ImageUploadService } from './services/image-upload.service';
import { EmailPreviewHydrationData, PreviewRenderingService } from './services/preview-rendering.service';

export { AppListingServiceInterface, EMAIL_BUILDER_APP_LISTING_SERVICE_TOKEN, EMAIL_BUILDER_ENABLED_RECIPES };

export { EmailBuilderComponent, HasUnsavedChangesGuard, PreviewRenderingService };

export { EmailSetup };

export {
  ALL_ADVANCED_SECTIONS_ENABLED,
  AdvancedSections,
} from './components/email-advanced-settings/email-advanced-settings.component';

export { OptionalBlockIds } from './types/contentblocks';

export { EMAILBUILDER_UPLOADER_SERVICE_TOKEN, EmailContentData, EmailPreviewHydrationData };

const MODULE_IMPORTS = [
  CommonModule,
  MatIconModule,
  MatCheckboxModule,
  MatButtonModule,
  MatButtonToggleModule,
  GalaxyTooltipModule,
  MatExpansionModule,
  MatMenuModule,
  MatDividerModule,
  MatTooltipModule,
  MatTabsModule,
  FormsModule,
  DragDropModule,
  CdkScrollableModule,
  GalaxyResizeModule,
  GalaxyButtonGroupModule,
  GalaxyFormFieldModule,
  GalaxyUploaderModule,
  GalaxySnackbarModule,
  GalaxyLoadingSpinnerModule,
  GalaxyButtonLoadingIndicatorModule,
  MatInputModule,
  MatProgressSpinnerModule,
  MatDialogModule,
  MatSelectModule,
  MatAutocompleteModule,
  ReactiveFormsModule,
  TranslateModule,
  EmailEditorPreviewModule,
  EmailUII18nModule,
  LexiconModule.forChild(),
  DynamicComponentSelectorModule,
  UnsavedChangesDialogComponent,
  CoreModule,
  GalaxySnackbarModule,
  ImageGenerationDialogComponent,
];

@NgModule({
  declarations: [
    EmailBuilderComponent,
    EmailAdvancedSettingsComponent,
    EmailBlocksListComponent,
    EmailBlockPaddingComponent,
    ImageBlockComponent,
    HeadingBlockComponent,
    RichTextBlockComponent,
    ButtonBlockComponent,
    DividerBlockComponent,
    SpacerBlockComponent,
    HtmlBlockComponent,
    HoldShiftToChangeNumberFieldByTenDirective,
    RichTextEditorComponent,
    ContactCardComponent,
    SimpleTableBlockComponent,
    ImageAndTextBlockComponent,
    SuggestContentComponent,
    ContainsValidDynamicContentDirective,
  ],
  imports: [...MODULE_IMPORTS, ReviewRequestLinksBlockComponent, NpsBlockComponent, GalaxyBadgeModule],
  exports: [EmailBuilderComponent],
  providers: [
    ContentGenerationService,
    PreviewRenderingService,
    HasUnsavedChangesGuard,
    ImageUploadService,
    FeatureFlagEnabledService,
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class EmailBuilderModule {}
